import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { User } from '../model/user';
import { Router } from '@angular/router';
import { AppService } from './app.service';

/**
 * connect-service's `ApiResponse` envelope as the password-reset routes answer it.
 * They always answer HTTP 200 and carry the outcome in `code` (200, 400, 409 or
 * 410) and `status`; `data` is an empty string unless the route returns a value.
 */
export interface PasswordResetResponse<T = never> {
  code: number;
  status: 'SUCCESS' | 'FAILURE';
  message: string;
  data: T | '';
}

/** What a successful redeem returns: the account's address, for the login page. */
export interface RedeemedAccount {
  email: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private tokenKey = 'token';

  constructor(
    private http: HttpClient,
    private router: Router,
    private appService: AppService
  ) {}

  public login(email: string, password: string): Observable<any> {
    return this.http.post(
      environment.apiUrl + '/user/login',
      {
        email: email?.toLowerCase(),
        password: password,
      },
      { responseType: 'json' }
    );
  }

  public register(register: User): Observable<any> {
    const smsOptIn = !!register.usePhoneForSms;
    return this.http.post<User>(environment.apiUrl + '/user/register', {
      firstName: register.firstName,
      lastName: register.lastName,
      password: register.password,
      email: register.email?.toLowerCase(),
      phone: register.phone,
      location: register.location,
      usePhoneForSms: smsOptIn,
      notifyBySms: smsOptIn,
      referredBy: register.referredBy,
    });
  }

  public getToken(): string | null {
    return this.isLoggedIn()
      ? localStorage.getItem(this.tokenKey) ||
          sessionStorage.getItem(this.tokenKey)
      : null;
  }
  public isLoggedIn(): boolean {
    let token =
      localStorage.getItem(this.tokenKey) ||
      sessionStorage.getItem(this.tokenKey);
    return !!token;
  }

  isTokenExpired(): boolean {
    var expiry = 0;
    let token =
      localStorage.getItem(this.tokenKey) ||
      sessionStorage.getItem(this.tokenKey);
    if (token != null) {
      expiry = JSON.parse(atob(token.split('.')[1])).exp;
    }
    return expiry - Math.floor(new Date().getTime() / 1000) <= 0;
  }

  public newPassword(
    email: string,
    password: string,
    newPassword: string
  ): Observable<any> {
    const token = localStorage.getItem(this.tokenKey);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const encodedEmail = encodeURIComponent(email.toString());
    return this.http.put(
      environment.apiUrl + '/user/new-password',
      {
        email: email?.toLowerCase(),
        password: password,
        newPassword: newPassword,
      },
      { headers: headers, responseType: 'json' }
    );
  }

  // The four password-reset routes are public and send no Authorization header:
  // a browser can still hold an expired session token, and a link must work there.
  // The token travels only in the POST body, never in a URL.

  public requestPasswordReset(email: string): Observable<PasswordResetResponse> {
    return this.http.post<PasswordResetResponse>(
      environment.apiUrl + '/user/password-reset/request',
      { email: email?.toLowerCase() }
    );
  }

  public validateResetToken(token: string): Observable<PasswordResetResponse> {
    return this.http.post<PasswordResetResponse>(
      environment.apiUrl + '/user/password-reset/validate',
      { token: token }
    );
  }

  public redeemResetToken(
    token: string,
    newPassword: string
  ): Observable<PasswordResetResponse<RedeemedAccount>> {
    return this.http.post<PasswordResetResponse<RedeemedAccount>>(
      environment.apiUrl + '/user/password-reset/redeem',
      { token: token, newPassword: newPassword }
    );
  }

  public resendResetLink(token: string): Observable<PasswordResetResponse> {
    return this.http.post<PasswordResetResponse>(
      environment.apiUrl + '/user/password-reset/resend',
      { token: token }
    );
  }

  logout() {
    localStorage.clear();
    sessionStorage.clear();
    this.appService.setRedirectUrl('');
    this.router.navigate(['login']);
    window.location.reload();
  }
}
